/**
 * Totem Agent Service
 *
 * Service for interacting with the local Totem Agent running on the Raspberry Pi
 * Base URL: http://127.0.0.1:3001
 *
 * Note: The totem container uses network_mode: host, so 127.0.0.1 refers to the host machine
 */

import axios, { type AxiosInstance, type AxiosResponse } from 'axios';
import type { TotemConfigResponse, TotemRenewRequest, TotemRenewResponse } from './totemAppTypes';

class TotemAgentService {
  private readonly client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: 'http://127.0.0.1:3001',
      timeout: 95000, // Slightly more than the 90s timeout of the agent
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  /**
   * Get current totem configuration
   * GET /config
   */
  async getConfig(): Promise<TotemConfigResponse> {
    try {
      const response = await this.client.get<TotemConfigResponse>('/config');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Renew/Register totem
   * POST /renew
   */
  async renewTotem(data: TotemRenewRequest): Promise<TotemRenewResponse> {
    try {
      const response = await this.client.post<TotemRenewResponse>('/renew', data);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Login Student / Identify User
   * POST /renew (reused endpoint on agent)
   */
  async loginStudent(data: any, token: string): Promise<any> {
    try {
      const response = await this.client.post('/renew', data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }
  /**
   * Verify Face
   * POST /face/verify
   */
  async verifyFace(
    studentId: string,
    photoId: string,
    token?: string,
  ): Promise<{
    ok: boolean;
    match: boolean;
    student_id?: string;
    confidence: number;
    brightness?: number;
    texture?: number;
    reason?: string;
    error?: boolean;
    user_message?: string;
  }> {
    try {
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await this.client.post(
        '/face/verify',
        {
          student_id: studentId,
          photo_id: photoId,
        },
        { headers },
      );

      return response.data;
    } catch (error: any) {
      // Se o agente retornar erro HTTP (4xx/5xx) mas com body útil,
      // normalizamos para o mesmo formato de sucesso com ok=false,
      // para que a tela consiga exibir user_message/reason em vez de erro genérico.
      if (error?.response?.data) {
        const data = error.response.data as {
          ok?: boolean;
          match?: boolean;
          student_id?: string;
          confidence?: number;
          brightness?: number;
          texture?: number;
          reason?: string;
          error?: boolean;
          user_message?: string;
        };

        return {
          ok: data.ok ?? false,
          match: data.match ?? false,
          student_id: data.student_id,
          confidence: data.confidence ?? 0,
          brightness: data.brightness,
          texture: data.texture,
          reason: data.reason,
          error: data.error ?? true,
          user_message: data.user_message ?? data.reason ?? 'Não foi possível validar sua identidade',
        };
      }

      // Se não houver response (erro de rede / timeout), propagamos para ser tratado como
      // erro de conexão na tela.
      throw error;
    }
  }
}

export const totemAgentService = new TotemAgentService();
export { TotemAgentService };
